/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
  message,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import currencyFormatter from 'utils/currencyFormatter';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { getPuestos } from 'api/catalogos/puestos';
import {
  getPercepcionesPuestos,
  postPercepcionPuesto,
  patchPercepcionPuesto,
  deletePercepcionPuesto,
  getPercepcionPuesto,
} from 'api/catalogos/percepcionesPuestos';
import { ESTATUS_ACTIVO } from 'utils/constants';
import moment from 'moment';

const PercepcionImportePuesto = ({ percepcionId, permission, disabled }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [disableFields, setDisableFields] = useState(false);

  const formInstances = [form];

  const fetchData = async () => {
    if (!percepcionId) {
      setData({ results: [] });
      return;
    }
    setLoading(true);
    try {
      const _data = await getPercepcionesPuestos({ percepcion: percepcionId });
      setData(formatReceived(_data));
    } catch (err) {
      onError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [_estados, _puestos] = await Promise.all([
          getEstadosGlobales(),
          getPuestos().catch(() => []),
        ]);

        if (mounted) {
          setEstados(_estados);

          const listaPuestos = _puestos.results || _puestos || [];
          setPuestos(Array.isArray(listaPuestos) ? listaPuestos : []);

          if (percepcionId) {
            await fetchData();
          }
        }
      } catch (err) {
        onError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAll();

    return () => { mounted = false; };
  }, [percepcionId]);

  const onCancel = () => {
    setVisible(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    try {
      const [key] = selectedRowKeys;
      const listaDatos = data.results || (Array.isArray(data) ? data : []);
      const match = responseData || listaDatos.find((item) => item.id === key);

      if (!match) return;

      const _record = await getPercepcionPuesto(match.id);

      if (_record) {
        form.resetFields();
        const formatted = formatReceived(_record);

        // eslint-disable-next-line max-len
        if (formatted.vigencia_inicial) formatted.vigencia_inicial = moment(formatted.vigencia_inicial);
        if (formatted.vigencia_final) formatted.vigencia_final = moment(formatted.vigencia_final);

        // Extraer ID si viene objeto en 'puesto'
        if (typeof formatted.puesto === 'object') formatted.puesto = formatted.puesto?.id;

        form.setFieldsValue(formatted);
        setDisableFields(_record?.estados_globales !== ESTATUS_ACTIVO);
        setVisible(true);
      }
    } catch (err) {
      onError(err);
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      values.percepcion = percepcionId;

      if (values.vigencia_inicial) values.vigencia_inicial = values.vigencia_inicial.format('YYYY-MM-DD');
      if (values.vigencia_final) values.vigencia_final = values.vigencia_final.format('YYYY-MM-DD');

      const [key] = selectedRowKeys;
      const saved = await (key ? patchPercepcionPuesto(key, values) : postPercepcionPuesto(values));

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePercepcionPuesto(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    if (!percepcionId) {
      message.warning('Debes guardar la Percepción antes de agregar puestos.');
      return;
    }
    form.resetFields();
    onCancel();
    form.setFieldsValue({
      estados_globales: ESTATUS_ACTIVO,
    });
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Puesto',
      dataIndex: 'puesto',
      key: 'puesto',
      width: 350,
      render: (val) => {
        if (typeof val === 'object' && val !== null) {
          return val.clave_y_descripcion || val.descripcion || val.nombre || val.id;
        }
        const listaSegura = Array.isArray(puestos) ? puestos : [];
        const p = listaSegura.find((item) => item.id === val);
        if (p) {
          return p.clave_y_descripcion || `${p.clave || ''} - ${p.descripcion || p.nombre || ''}`;
        }
        return val;
      },
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 150,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Vigencia Inicial',
      dataIndex: 'vigencia_inicial',
      key: 'vigencia_inicial',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = { required: true, message: 'Campo requerido' };

  return (
    <Row style={{ marginTop: 20 }}>
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          // Habilitamos solo si hay ID padre
          disabled={disabled || !percepcionId}
          allowExportPDF={false}
        />

        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          width={800}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Importe por Puesto`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="formPuesto"
            initialValues={{ estados_globales: ESTATUS_ACTIVO }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="puesto" label="Puesto" rules={[requiredRule]} hasFeedback>
                  <Select
                    dataSource={puestos}
                    keyProp="id"
                    labelProp="descripcion"
                    renderLabel={(item) => item.clave_y_descripcion || `${item.clave || ''} - ${item.descripcion || item.nombre}`}
                    showSearch
                    disabled={disableFields}
                    filterProp="descripcion"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="importe" label="Importe" rules={[requiredRule]} hasFeedback>
                  <Input prefix="$" type="number" step="0.01" disabled={disableFields} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="vigencia_inicial" label="Vigencia Inicial" rules={[requiredRule]} hasFeedback>
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabled={disableFields} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="vigencia_final" label="Vigencia Final" hasFeedback>
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabled={disableFields} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="estados_globales" label="Estatus" rules={[requiredRule]} hasFeedback>
                  <Select
                    dataSource={estados}
                    disabled={selectedRowKeys.length <= 0 || disableFields}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden><Button htmlType="submit" /></Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="¿Eliminar este registro de Puesto?"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PercepcionImportePuesto;
